import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { medicsAPI, exportAPI, jobTypesAPI, contractsAPI, auditAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Edit, 
  Trash2, 
  FileText,
  Phone,
  Mail,
  User,
  Download,
  Briefcase,
  Shield,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { trackRecentView } from '../hooks/useRecentlyViewed';

const COMPLIANCE_STATUS_STYLES = {
  compliant: { style: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20', label: 'Compliant' },
  expiring_soon: { style: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20', label: 'Expiring Soon' },
  expired: { style: 'bg-red-500/15 text-red-400 border-red-500/20', label: 'Expired' },
  pending: { style: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20', label: 'Pending' },
};

export default function Medics() {
  const { user, hasManagerAccess, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [medics, setMedics] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Initialize status filter from URL params (e.g., ?status=compliant)
  const [statusFilter, setStatusFilter] = useState(() => {
    const urlStatus = searchParams.get('status');
    return urlStatus || 'all';
  });
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showJobTypesModal, setShowJobTypesModal] = useState(false);
  const [selectedMedic, setSelectedMedic] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedJobTypeIds, setSelectedJobTypeIds] = useState([]);
  const [allowanceHistory, setAllowanceHistory] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'Paramedic',
    notes: '',
    job_type_ids: [],
    annual_leave_allowance: 28,
  });

  const [docData, setDocData] = useState({
    type: 'certification',
    name: '',
    expiry_date: '',
    file_url: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.compliance_status = statusFilter;
      if (jobTypeFilter !== 'all') params.job_type_id = jobTypeFilter;
      
      const [medicsRes, jobTypesRes, contractsRes] = await Promise.all([
        medicsAPI.list(params),
        jobTypesAPI.list(),
        contractsAPI.list(),
      ]);
      setMedics(medicsRes.data);
      setJobTypes(jobTypesRes.data);
      setContracts(contractsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, jobTypeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    try {
      await medicsAPI.create({
        ...formData,
        job_type_ids: formData.job_type_ids || [],
      });
      toast.success('Medic created successfully');
      setShowCreateModal(false);
      setFormData({ name: '', email: '', phone: '', role: 'Paramedic', notes: '', job_type_ids: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create medic');
    }
  };

  const handleEdit = async () => {
    if (!selectedMedic) return;
    try {
      const payload = {
        ...formData,
        job_type_ids: formData.job_type_ids || [],
      };
      // Coerce allowance to a positive int or omit if cleared
      if (payload.annual_leave_allowance === '' || payload.annual_leave_allowance === null || payload.annual_leave_allowance === undefined) {
        delete payload.annual_leave_allowance;
      } else {
        payload.annual_leave_allowance = parseInt(payload.annual_leave_allowance, 10);
      }
      await medicsAPI.update(selectedMedic.id, payload);
      toast.success('Medic updated successfully');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update medic');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this medic?')) return;
    try {
      await medicsAPI.delete(id);
      toast.success('Medic deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete medic');
    }
  };

  const handleAddDocument = async () => {
    if (!selectedMedic) return;
    try {
      await medicsAPI.addDocument(selectedMedic.id, docData);
      toast.success('Document added successfully');
      setShowDocModal(false);
      setDocData({ type: 'certification', name: '', expiry_date: '', file_url: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add document');
    }
  };

  const openEditModal = (medic) => {
    setSelectedMedic(medic);
    trackRecentView(user?.id, {
      type: 'medic',
      id: medic.id,
      label: medic.name || 'Unnamed medic',
      subtitle: medic.role || medic.email,
      href: '/medics',
    });
    setFormData({
      name: medic.name,
      email: medic.email,
      phone: medic.phone,
      role: medic.role,
      notes: medic.notes || '',
      job_type_ids: medic.job_type_ids || [],
      annual_leave_allowance: medic.annual_leave_allowance ?? 28,
    });
    setAllowanceHistory([]);
    setShowEditModal(true);
    // Allowance history — best effort, only visible to roles with audit access.
    if (hasManagerAccess || isAdmin) {
      auditAPI.list({ action: 'MEDIC_UPDATED', medic_id: medic.id, limit: 50 })
        .then(({ data }) => {
          const entries = (data || []).filter(
            (e) => e?.details && Object.prototype.hasOwnProperty.call(e.details, 'annual_leave_allowance')
          );
          setAllowanceHistory(entries.slice(0, 3));
        })
        .catch(() => setAllowanceHistory([]));
    }
  };

  const openJobTypesModal = (medic) => {
    setSelectedMedic(medic);
    setSelectedJobTypeIds(medic.job_type_ids || []);
    setShowJobTypesModal(true);
  };

  const handleSaveJobTypes = async () => {
    if (!selectedMedic) return;
    try {
      await medicsAPI.update(selectedMedic.id, { job_type_ids: selectedJobTypeIds });
      toast.success('Job type eligibility updated');
      setShowJobTypesModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job types');
    }
  };

  const toggleJobType = (jobTypeId) => {
    setSelectedJobTypeIds(prev => 
      prev.includes(jobTypeId)
        ? prev.filter(id => id !== jobTypeId)
        : [...prev, jobTypeId]
    );
  };

  const openDocModal = (medic) => {
    setSelectedMedic(medic);
    setShowDocModal(true);
  };

  const handleCSVExport = async () => {
    setExportLoading(true);
    try {
      const baseUrl = exportAPI.getCSVUrl('medics');
      const response = await fetch(baseUrl, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `medics_export.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const filteredMedics = medics.filter(medic => 
    medic.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    medic.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="medics-container">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex flex-col sm:flex-row flex-1 gap-2 sm:gap-3">
          <div className="relative flex-1 sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input
              placeholder="Search medics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#2B2B2B] border-white/10 text-[#F2F2F2]"
              data-testid="search-medics-input"
            />
          </div>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="flex-1 sm:w-[150px] bg-[#2B2B2B] border-white/10" data-testid="status-filter-select">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#2B2B2B] border-white/10">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="compliant">Compliant</SelectItem>
                <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
              <SelectTrigger className="flex-1 sm:w-[180px] bg-[#2B2B2B] border-white/10" data-testid="job-type-filter-select">
                <SelectValue placeholder="All Job Types" />
              </SelectTrigger>
              <SelectContent className="bg-[#2B2B2B] border-white/10">
                <SelectItem value="all">All Job Types</SelectItem>
                {jobTypes.map(jt => (
                  <SelectItem key={jt.id} value={jt.id}>{jt.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleCSVExport}
            disabled={exportLoading}
            className="flex-1 lg:flex-none border-[#1ABC9C] text-[#1ABC9C] hover:bg-[#1ABC9C]/10"
            data-testid="export-medics-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            className="flex-1 lg:flex-none bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 text-white"
            onClick={() => setShowCreateModal(true)}
            data-testid="add-medic-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Medic
          </Button>
        </div>
      </div>

      {/* Medics Table (desktop) */}
      <Card className="bg-[#2B2B2B] border-white/5 hidden lg:block">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#A1A1AA]">Name</TableHead>
                <TableHead className="text-[#A1A1AA]">Contact</TableHead>
                <TableHead className="text-[#A1A1AA]">Role</TableHead>
                <TableHead className="text-[#A1A1AA]">Job Types</TableHead>
                <TableHead className="text-[#A1A1AA]">Status</TableHead>
                <TableHead className="text-[#A1A1AA]">Documents</TableHead>
                <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMedics.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-[#A1A1AA] py-8">
                    No medics found
                  </TableCell>
                </TableRow>
              ) : (
                filteredMedics.map((medic) => (
                  <TableRow 
                    key={medic.id} 
                    className="border-white/5 hover:bg-white/5"
                    data-testid={`medic-row-${medic.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1ABC9C]/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-[#1ABC9C]" />
                        </div>
                        <span className="font-medium text-[#F2F2F2]">{medic.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                          <Mail className="w-3 h-3" />
                          {medic.email}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                          <Phone className="w-3 h-3" />
                          {medic.phone || 'N/A'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#F2F2F2]">{medic.role}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {medic.job_type_ids?.length > 0 ? (
                          medic.job_type_ids.slice(0, 2).map(jtId => {
                            const jt = jobTypes.find(j => j.id === jtId);
                            return jt ? (
                              <Badge key={jtId} variant="outline" className="text-xs border-white/20 text-[#A1A1AA]">
                                {jt.name}
                              </Badge>
                            ) : null;
                          })
                        ) : (
                          <span className="text-[#A1A1AA] text-sm">None</span>
                        )}
                        {medic.job_type_ids?.length > 2 && (
                          <Badge variant="outline" className="text-xs border-white/20 text-[#A1A1AA]">
                            +{medic.job_type_ids.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={COMPLIANCE_STATUS_STYLES[medic.compliance_status]?.style || COMPLIANCE_STATUS_STYLES.pending.style}>
                        {COMPLIANCE_STATUS_STYLES[medic.compliance_status]?.label || 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-[#A1A1AA]">{medic.documents?.length || 0} docs</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-[#A1A1AA] hover:text-[#F2F2F2]">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#2B2B2B] border-white/10">
                          <DropdownMenuItem onClick={() => openEditModal(medic)} className="text-[#F2F2F2] focus:bg-white/10">
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openJobTypesModal(medic)} className="text-[#F2F2F2] focus:bg-white/10">
                            <Briefcase className="w-4 h-4 mr-2" />
                            Job Types
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openDocModal(medic)} className="text-[#F2F2F2] focus:bg-white/10">
                            <FileText className="w-4 h-4 mr-2" />
                            Add Document
                          </DropdownMenuItem>
                          {hasManagerAccess() && (
                            <DropdownMenuItem onClick={() => handleDelete(medic.id)} className="text-red-400 focus:bg-white/10">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Medics Cards (mobile / tablet) */}
      <div className="lg:hidden space-y-3" data-testid="medics-mobile-list">
        {filteredMedics.length === 0 ? (
          <Card className="bg-[#2B2B2B] border-white/5">
            <CardContent className="p-6 text-center text-[#A1A1AA]">
              No medics found
            </CardContent>
          </Card>
        ) : (
          filteredMedics.map((medic) => {
            const complianceStyle = COMPLIANCE_STATUS_STYLES[medic.compliance_status] || COMPLIANCE_STATUS_STYLES.pending;
            const jobTypeNames = (medic.job_type_ids || [])
              .map(id => jobTypes.find(j => j.id === id)?.name)
              .filter(Boolean);
            return (
              <Card
                key={medic.id}
                className="bg-[#2B2B2B] border-white/5"
                data-testid={`medic-card-${medic.id}`}
              >
                <CardContent className="p-4 space-y-3">
                  {/* Top row: avatar, name, status, actions */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-full bg-[#1ABC9C]/20 flex items-center justify-center shrink-0">
                        <User className="w-5 h-5 text-[#1ABC9C]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-[#F2F2F2] truncate">{medic.name}</p>
                        <p className="text-xs text-[#A1A1AA] truncate">{medic.role}</p>
                      </div>
                    </div>
                    <Badge className={`${complianceStyle.style} shrink-0`}>
                      {complianceStyle.label}
                    </Badge>
                  </div>

                  {/* Contact */}
                  <div className="space-y-1 text-sm text-[#A1A1AA]">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3 shrink-0" />
                      <span className="truncate">{medic.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-3 h-3 shrink-0" />
                      <span>{medic.phone || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Job types */}
                  {jobTypeNames.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {jobTypeNames.slice(0, 3).map(name => (
                        <Badge key={name} variant="outline" className="text-xs border-white/20 text-[#A1A1AA]">
                          {name}
                        </Badge>
                      ))}
                      {jobTypeNames.length > 3 && (
                        <Badge variant="outline" className="text-xs border-white/20 text-[#A1A1AA]">
                          +{jobTypeNames.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Footer: docs count + primary action + overflow menu */}
                  <div className="flex items-center justify-between pt-2 border-t border-white/5">
                    <span className="text-xs text-[#A1A1AA]">
                      {medic.documents?.length || 0} documents
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        onClick={() => openEditModal(medic)}
                        className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 h-8"
                        data-testid={`medic-card-edit-${medic.id}`}
                      >
                        <Edit className="w-3.5 h-3.5 mr-1" />
                        Edit
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-[#A1A1AA] hover:text-[#F2F2F2]"
                            data-testid={`medic-card-menu-${medic.id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#2B2B2B] border-white/10">
                          <DropdownMenuItem onClick={() => openJobTypesModal(medic)} className="text-[#F2F2F2] focus:bg-white/10">
                            <Briefcase className="w-4 h-4 mr-2" />
                            Job Types
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openDocModal(medic)} className="text-[#F2F2F2] focus:bg-white/10">
                            <FileText className="w-4 h-4 mr-2" />
                            Add Document
                          </DropdownMenuItem>
                          {hasManagerAccess() && (
                            <DropdownMenuItem onClick={() => handleDelete(medic.id)} className="text-red-400 focus:bg-white/10">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Medic Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Add New Medic</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
                data-testid="medic-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
                data-testid="medic-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
                data-testid="medic-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}>
                <SelectTrigger className="bg-[#1A1A1A] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="Paramedic">Paramedic</SelectItem>
                  <SelectItem value="EMT">EMT</SelectItem>
                  <SelectItem value="Nurse">Nurse</SelectItem>
                  <SelectItem value="Doctor">Doctor</SelectItem>
                  <SelectItem value="First Aider">First Aider</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleCreate} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90" data-testid="save-medic-btn">
              Create Medic
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Medic Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Edit Medic</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}>
                <SelectTrigger className="bg-[#1A1A1A] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="Paramedic">Paramedic</SelectItem>
                  <SelectItem value="EMT">EMT</SelectItem>
                  <SelectItem value="Nurse">Nurse</SelectItem>
                  <SelectItem value="Doctor">Doctor</SelectItem>
                  <SelectItem value="First Aider">First Aider</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Compliance Status</Label>
              <Select 
                value={formData.compliance_status || selectedMedic?.compliance_status} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, compliance_status: value }))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(hasManagerAccess || isAdmin) && (
              <div className="space-y-2">
                <Label htmlFor="annual-leave-allowance">Annual Leave Allowance (days)</Label>
                <Input
                  id="annual-leave-allowance"
                  type="number"
                  min={0}
                  max={365}
                  value={formData.annual_leave_allowance ?? 28}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const parsed = raw === '' ? '' : Math.max(0, parseInt(raw, 10) || 0);
                    setFormData(prev => ({ ...prev, annual_leave_allowance: parsed }));
                  }}
                  className="bg-[#1A1A1A] border-white/10"
                  data-testid="edit-medic-allowance-input"
                />
                <p className="text-xs text-[#A1A1AA]">
                  Default is 28 days. Changes apply immediately to this medic's leave balance.
                </p>
                {allowanceHistory.length > 0 && (
                  <div
                    className="mt-2 rounded-md border border-white/5 bg-[#1A1A1A]/60 px-3 py-2 text-xs text-[#A1A1AA] space-y-1"
                    data-testid="allowance-history-log"
                  >
                    <div className="font-medium text-[#C7C7C7]">Recent changes</div>
                    {allowanceHistory.map((entry) => {
                      const ts = entry.timestamp ? new Date(entry.timestamp) : null;
                      const when = ts && !isNaN(ts.getTime())
                        ? ts.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                          + ' · ' + ts.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
                        : '—';
                      const val = entry.details?.annual_leave_allowance;
                      return (
                        <div
                          key={entry.id}
                          className="flex items-center justify-between gap-2"
                          data-testid={`allowance-history-entry-${entry.id}`}
                        >
                          <span className="truncate">
                            Set to <span className="text-[#1ABC9C] font-medium">{val}</span> by {entry.user_name || 'Unknown'}
                          </span>
                          <span className="shrink-0 text-[#71717A]">{when}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleEdit} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Document Modal */}
      <Dialog open={showDocModal} onOpenChange={setShowDocModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Add Document for {selectedMedic?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={docData.type} onValueChange={(value) => setDocData(prev => ({ ...prev, type: value }))}>
                <SelectTrigger className="bg-[#1A1A1A] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                  <SelectItem value="certification">Certification</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Document Name</Label>
              <Input
                value={docData.name}
                onChange={(e) => setDocData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., First Aid Certificate"
                className="bg-[#1A1A1A] border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={docData.expiry_date}
                onChange={(e) => setDocData(prev => ({ ...prev, expiry_date: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>File URL (Optional)</Label>
              <Input
                value={docData.file_url}
                onChange={(e) => setDocData(prev => ({ ...prev, file_url: e.target.value }))}
                placeholder="https://..."
                className="bg-[#1A1A1A] border-white/10"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDocModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleAddDocument} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90">
              Add Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Types Modal */}
      <Dialog open={showJobTypesModal} onOpenChange={setShowJobTypesModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#1ABC9C]" />
              Job Type Eligibility
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Select which job types {selectedMedic?.name} is qualified to work on.
              Medics can only be assigned to shifts matching their eligible job types.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[400px] pr-4">
            <div className="space-y-2 py-4">
              {jobTypes.length === 0 ? (
                <p className="text-[#A1A1AA] text-center py-4">No job types configured</p>
              ) : (
                jobTypes.map((jt) => (
                  <div 
                    key={jt.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedJobTypeIds.includes(jt.id)
                        ? 'border-[#1ABC9C]/50 bg-[#1ABC9C]/10'
                        : 'border-white/10 bg-[#1A1A1A] hover:border-white/20'
                    }`}
                    onClick={() => toggleJobType(jt.id)}
                  >
                    <Checkbox 
                      checked={selectedJobTypeIds.includes(jt.id)}
                      onCheckedChange={() => toggleJobType(jt.id)}
                      className="border-white/20 data-[state=checked]:bg-[#1ABC9C] data-[state=checked]:border-[#1ABC9C]"
                    />
                    <div className="flex-1">
                      <p className="text-[#F2F2F2] font-medium">{jt.name}</p>
                      {jt.description && (
                        <p className="text-sm text-[#A1A1AA]">{jt.description}</p>
                      )}
                    </div>
                    {selectedJobTypeIds.includes(jt.id) && (
                      <CheckCircle className="w-5 h-5 text-[#1ABC9C]" />
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
          <div className="flex items-center gap-2 p-3 bg-[#1A1A1A] rounded-lg">
            <Shield className="w-4 h-4 text-[#A1A1AA]" />
            <span className="text-sm text-[#A1A1AA]">
              {selectedJobTypeIds.length} job type{selectedJobTypeIds.length !== 1 ? 's' : ''} selected
            </span>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowJobTypesModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleSaveJobTypes} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
